import { storeToRefs } from 'pinia';
import SignInModal from '@/components/SignInModal.vue';
import SignUpModal from '@/components/SignUpModal.vue';
import SignUpModalMobile from '@/components/SignUpModalMobile.vue';
import { useRedirect } from '@/composables/useRedirect';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useModal, ModalOptions } from '@/storybook/modal/useModal';
import { useVfm } from 'vue-final-modal';
import { useMobileDetection } from './useMobileDetection';

export interface AuthModalProps {
  hideCloseButton?: boolean;
  signup: {
    sidebarText?: string;
    redirectUrl?: string;
    showModalForFreeTier?: boolean;
    signedUpFrom?: string;
  };
}

export const useAuthModals = () => {
  const vfm = useVfm();
  const { isCustomDomain, loginUrl, signupUrl } = storeToRefs(useSettingsStore());
  const { getURLToBaseDomain } = useRedirect();
  const { isMobile } = useMobileDetection();

  const showSignInModal = (redirectUrl?: string, ignoreMobile: boolean = false, modalOptions: ModalOptions = {}) => {
    vfm.closeAll();

    if (isCustomDomain?.value && loginUrl?.value) {
      let url = loginUrl?.value;

      if (redirectUrl) {
        url = `${url}?return_to=${redirectUrl}`;
      }

      window.location.assign(url);
      return Promise.resolve();
    }

    const redirect = getURLToBaseDomain('login');

    if (redirect) {
      window.location.assign(redirect);
      return;
    }

    const isMobileDetected = isMobile.value && !ignoreMobile;

    if (isMobileDetected) {
      modalOptions.hideClose = true;
      modalOptions.noBorder = true;
    }

    const { open } = useModal(isMobileDetected ? SignUpModalMobile : SignInModal, { ...modalOptions, modalProps: { redirectUrl } });
    return open();
  };

  const showSignUpModal = (
    { sidebarText, redirectUrl, showModalForFreeTier, signedUpFrom }: AuthModalProps['signup'] = {},
    modalOptions: ModalOptions = {}
  ) => {
    vfm.closeAll();

    if (isCustomDomain?.value && signupUrl?.value) {
      let url = signupUrl?.value;

      if (redirectUrl) {
        url = `${url}?return_to=${redirectUrl}`;
      }

      window.location.assign(url);
      return Promise.resolve();
    }

    const redirect = getURLToBaseDomain('signup');

    if (redirect) {
      window.location.assign(redirect);
      return;
    }

    const { open } = useModal(SignUpModal, { ...modalOptions, modalProps: { sidebarText, redirectUrl, showModalForFreeTier, signedUpFrom } });
    return open();
  };

  return { showSignInModal, showSignUpModal };
};
